/**
 * Schema.org structured data generator
 *
 * This utility helps create structured data in JSON-LD format for the personal website,
 * improving SEO and enabling rich results in search engines.
 */

/**
 * Generate JSON-LD for the Person (Marcelo Mira)
 */
export function generatePersonSchema(options?: {
  name?: string;
  jobTitle?: string;
  description?: string;
  url?: string;
  image?: string;
  email?: string;
  sameAs?: string[];
}): Record<string, unknown> {
  const person = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    'name': options?.name || 'Marcelo Ariel Mira Marin',
    'alternateName': 'Mars',
    'jobTitle': options?.jobTitle || 'Full-Stack Web Developer & UX/UI Designer',
    'description': options?.description || 'Self-taught full-stack web developer and UX/UI designer with 26+ years of experience, specializing in Ember.js, TypeScript, and modern web technologies.',
    'url': options?.url || 'https://marcemira.com',
    'image': options?.image || 'https://marcemira.com/img/marcelo_mira_3.jpg',
    'email': options?.email || 'hello@marcemira.com',
    'birthDate': '1986-12-05',
    'birthPlace': {
      '@type': 'Place',
      'address': {
        '@type': 'PostalAddress',
        'addressCountry': 'AR'
      }
    },
    'homeLocation': {
      '@type': 'Place',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Córdoba',
        'addressCountry': 'AR'
      }
    },
    'sameAs': options?.sameAs || [
      'https://www.linkedin.com/in/marcemira',
      'https://github.com/marcemira',
      'https://twitter.com/marcemira'
    ],
    'knowsAbout': [
      'Web Development',
      'JavaScript',
      'TypeScript',
      'Ember.js',
      'Node.js',
      'NestJS',
      'React',
      'HTML5',
      'CSS3',
      'Tailwind CSS',
      'UI/UX Design',
      'Fastboot',
      'Server-Side Rendering',
      'Frontend Development',
      'Backend Development'
    ],
    'knowsLanguage': [
      {
        '@type': 'Language',
        'name': 'Spanish',
        'alternateName': 'es'
      },
      {
        '@type': 'Language',
        'name': 'English',
        'alternateName': 'en'
      }
    ]
  };

  return person;
}

/**
 * Generate JSON-LD for the website
 */
export function generateWebsiteSchema(options?: {
  name?: string;
  url?: string;
  description?: string;
  author?: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': options?.name || 'Marcemira - Marcelo Mira Portfolio',
    'url': options?.url || 'https://marcemira.com',
    'description': options?.description || 'Personal portfolio of Marcelo Mira, a full-stack web developer and UX/UI designer specializing in Ember.js and modern web technologies.',
    'author': {
      '@type': 'Person',
      'name': options?.author || 'Marcelo Mira'
    },
    'inLanguage': ['en', 'es']
  };
}

/**
 * Generate JSON-LD for breadcrumb navigation
 */
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url
    }))
  };
}

/**
 * Generate JSON-LD for professional profile
 */
export function generateProfilePageSchema(options: {
  name: string;
  description: string;
  url: string;
  image?: string;
  skills?: string[];
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    'name': options.name,
    'description': options.description,
    'url': options.url,
    ...(options.image && { 'image': options.image }),
    'mainEntity': generatePersonSchema({
      name: options.name,
      description: options.description,
      url: options.url,
      image: options.image
    })
  };
}

/**
 * Generate JSON-LD for work experience (Organization)
 */
export function generateWorkExperienceSchema(options: {
  company: string;
  role: string;
  startDate: string;
  endDate?: string;
  description?: string;
  location?: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'OrganizationRole',
    'roleName': options.role,
    'startDate': options.startDate,
    ...(options.endDate && { 'endDate': options.endDate }),
    'memberOf': {
      '@type': 'Organization',
      'name': options.company,
      ...(options.location && {
        'location': {
          '@type': 'Place',
          'name': options.location
        }
      })
    },
    ...(options.description && { 'description': options.description })
  };
}

/**
 * Generate JSON-LD for an Organization (company/startup)
 */
export function generateOrganizationSchema(options: {
  name: string;
  url?: string;
  logo?: string;
  description?: string;
  foundingDate?: string;
  founder?: string;
  location?: string;
  sameAs?: string[];
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': options.name,
    ...(options.url && { 'url': options.url }),
    ...(options.logo && { 'logo': options.logo }),
    ...(options.description && { 'description': options.description }),
    ...(options.foundingDate && { 'foundingDate': options.foundingDate }),
    ...(options.founder && {
      'founder': {
        '@type': 'Person',
        'name': options.founder
      }
    }),
    ...(options.location && {
      'location': {
        '@type': 'Place',
        'name': options.location
      }
    }),
    ...(options.sameAs && { 'sameAs': options.sameAs })
  };
}

export default {
  generatePersonSchema,
  generateWebsiteSchema,
  generateBreadcrumbSchema,
  generateProfilePageSchema,
  generateWorkExperienceSchema,
  generateOrganizationSchema
};

